import type { TFunction } from 'i18next';
import { buildCsv } from 'utils/csv';
import { availabilityLabel } from 'utils/unavailability';
import { calendarDay } from 'utils/interventionDate';
import type { TechnicianActivity, TechnicianListItem } from 'api/techniciansApi';

/**
 * The CSV export the mock-up drew without a handler.
 *
 * `technicianFullName` lives here rather than beside the columns on purpose: the file and the
 * screen must spell a name the same way, and two implementations would drift.
 */

export const technicianFullName = (technician: TechnicianListItem): string =>
  [technician.profile.firstName, technician.profile.lastName].filter(Boolean).join(' ').trim() ||
  technician.username;

/**
 * A trustworthy count, or `null` when `activity` is missing, absent, or malformed — never a false
 * `0`. Guards the edge: a rolling deploy, a stale cache, or a partial payload can send no
 * `activity` at all just as easily as one with the wrong shape. The one place this is decided —
 * the record, the directory column and its CSV twin all call this instead of re-deriving it.
 */
export const activityCount = (activity?: TechnicianActivity | null): number | null => {
  const n = activity?.interventions;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : null;
};

/** The last intervention day, or `null` unless it is a real calendar day (`2026-02-31` is not). */
export const activityDay = (activity?: TechnicianActivity | null): string | null =>
  calendarDay(activity?.lastIntervention);

/** The visible columns in screen order. */
export const buildTechniciansCsv = (t: TFunction, technicians: TechnicianListItem[]): string => {
  const header = [
    t('Technician'),
    t('Email'),
    t('Phone'),
    t('Company'),
    t('Country'),
    t('Areas covered'),
    t('Specialties'),
    t('Expertises'),
    t('Average rating'),
    t('Ratings received'),
    t('Interventions'),
    t('Last intervention'),
    t('Availability'),
    t('Status'),
  ];

  const rows = technicians.map((technician) => {
    const count = activityCount(technician.activity);
    return [
      technicianFullName(technician),
      technician.email,
      technician.profile.phone ?? '',
      technician.profile.company ?? '',
      technician.profile.countryName ?? '',
      // Every value, never a "+38": a spreadsheet is where the whole list is wanted.
      technician.geoAreas.map((area) => area.code).join(', '),
      technician.specialties.map((specialty) => specialty.name).join(', '),
      technician.expertises.map((expertise) => expertise.name).join(', '),
      // Empty, not 0: nothing was rated.
      technician.ratings.avg == null ? '' : String(technician.ratings.avg),
      String(technician.ratings.count),
      // 0 stays 0: a measured count, unlike the blank above which means "nothing rated".
      count === null ? '' : String(count),
      activityDay(technician.activity) ?? '',
      // The word, not the periods: a spreadsheet cell holding three date ranges is unreadable, and
      // the record is where a period is looked at.
      availabilityLabel(t, technician.availability),
      technician.isActive ? t('Active') : t('Inactive'),
    ];
  });

  return buildCsv(header, rows);
};
